import React, { useMemo, useState } from "react";
import { DataMapPatchMutation } from "../../../../common/hooks/api/useDataMap";
import { useCollectionDetails } from "../../../../common/hooks/dataMapHooks";
import { recommendationsForDataRecipient } from "../../../../common/service/resources/data-map/dataMapHelpers";
import { useDataMapPendingDisclosedPicGroupIds } from "../../../../common/service/resources/data-map/dataMapPending";
import { useDataMapDisclosedPICGroups } from "../../../../common/service/resources/data-map/dataMapTransformers";
import { CollectionGroupDetailsDto } from "../../../../common/service/server/dto/CollectionGroupDto";
import {
  DataMapDto,
  PICGroupWithCategories,
} from "../../../../common/service/server/dto/datamap/DataMapDto";
import { OrganizationDataRecipientDto } from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { UUIDString } from "../../../../common/service/server/Types";
import { DescriptionCheckbox } from "../../../components/input/DescriptionCheckbox";
import { RecipientGuidance } from "../../../components/organisms/data-recipients/RecipientGuidance";
import { Modal, Box, Button, Typography } from "@mui/material";
import "./DataRecipientCollectionCheckboxes.scss";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Organization } from "../../../../common/models";

export const DESCRIPTION_OVERRIDES = {
  "online-identifiers":
    "E.g., IP address; device identifier; or other online, persistent, or unique identifiers, such as browser cookies",
  "internet-activity":
    "E.g., browsing or search history; interactions with websites, apps or ads; or email open and click-through rates",
};

type DataRecipientCollectionCheckboxesProps = {
  group: CollectionGroupDetailsDto;
  recipient: OrganizationDataRecipientDto;
  dataMap: DataMapDto;
  patchDataMap: DataMapPatchMutation;
  showRecommendations?: boolean;
  org: Organization;
};

export const DataRecipientCollectionCheckboxes: React.FC<
  DataRecipientCollectionCheckboxesProps
> = ({
  dataMap,
  patchDataMap,
  group: collectionGroup,
  recipient: dataRecipient,
  showRecommendations = true,
  org,
}) => {
  const { collectedPicGroups } = useCollectionDetails(dataMap, collectionGroup);
  const disclosedPicGroups = useDataMapDisclosedPICGroups(dataMap, {
    collectionGroupId: collectionGroup.id,
    vendorId: dataRecipient.vendorId,
  });
  const disclosedPicGroupIds = useMemo(
    () => disclosedPicGroups.map((g) => g.id),
    [disclosedPicGroups],
  );
  const pendingPicGroupIds = useDataMapPendingDisclosedPicGroupIds(dataMap, {
    collectionGroupId: collectionGroup.id,
    vendorId: dataRecipient.vendorId,
  });

  const { recommendedGroups } = useMemo(
    () => recommendationsForDataRecipient(dataMap, dataRecipient),
    [dataMap, dataRecipient],
  );

  const [showModal, setShowModal] = useState(false);
  const [lastCheckboxId, setLastCheckboxId] = useState<UUIDString | null>(null);

  const handleChange = (groupId: UUIDString, checked: boolean) => {
    if (!checked && disclosedPicGroupIds.length === 1) {
      setLastCheckboxId(groupId);
      setShowModal(true);
    } else {
      patchDataMap.mutate({
        operations: [
          {
            target: "DISCLOSURE",
            action: checked ? "ADD" : "REMOVE",
            groupId: collectionGroup.id,
            picGroupId: groupId,
            vendorId: dataRecipient.vendorId,
          },
        ],
      });
    }
  };

  const handleModalConfirm = () => {
    if (lastCheckboxId) {
      patchDataMap.mutate({
        operations: [
          {
            target: "DISCLOSURE",
            action: "REMOVE",
            groupId: collectionGroup.id,
            picGroupId: lastCheckboxId,
            vendorId: dataRecipient.vendorId,
          },
        ],
      });
    }
    setShowModal(false);
  };

  const handleModalCancel = () => {
    setShowModal(false);
    setLastCheckboxId(null);
  };

  const allowRecommendations = collectionGroup.collectionGroupType != "EMPLOYMENT";

  return (
    <>
      <RecipientGuidance recipient={dataRecipient} collectionGroup={collectionGroup} />

      {collectedPicGroups.map((g) => {
        const collected = g.categories.map((c) => c.name).join(", ");
        const collectedSet = new Set(g.categories.map((c) => c.id));
        const recommended =
          allowRecommendations &&
          showRecommendations &&
          recommendedGroups.some((rg: PICGroupWithCategories) => {
            return rg.id === g.id && rg.categories.some((cat) => collectedSet.has(cat.id));
          });
        const description = DESCRIPTION_OVERRIDES[g.key] ?? `You collect: ${collected}`;

        return (
          <DescriptionCheckbox
            className="mb-sm p-xs"
            key={g.id}
            label={g.name}
            description={description}
            checked={disclosedPicGroupIds.includes(g.id)}
            loading={pendingPicGroupIds.includes(g.id)}
            onChange={(_, checked) => handleChange(g.id, checked)}
            variant={recommended ? "light-purple" : undefined}
          />
        );
      })}

      <Modal
        open={showModal}
        onClose={handleModalCancel}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box className="modal-box">
          <Box className="modal-content">
            <span className="title-row">
              <Typography id="modal-title" className="modal-title">
                Remove {dataRecipient.name} as a data recipient for {org?.name}?
              </Typography>
              <XMarkIcon className="close-icon" onClick={handleModalCancel} />
            </span>
            <Typography id="modal-description" className="modal-body">
              You have unselected all the data categories disclosed to {dataRecipient.name} from{" "}
              {org?.name}. If you don’t wish to remove {dataRecipient.name} as a data recipient for
              this group, select at least one data category.
            </Typography>
          </Box>
          <Box className="modal-buttons">
            <Button onClick={handleModalCancel} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleModalConfirm} variant="contained" color="primary">
              Confirm
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};
